import { Box, ButtonBase, Stack, SxProps, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Body1Typography, Body2Typography } from "../../../../components/typography";
import { VisuallyHiddenInput } from "../../../../components/firebase-image-upload";
import { useNotification } from "../../../../contexts/NotificationContext";
import { compressImages } from "../../../../utils/image-compression";
import { useState } from "react";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

type SingleImageCallback = (image: { file: File; local_url: string }) => void;
type MultipleImagesCallback = (images: { file: File; local_url: string }[]) => void;

export const UploadProjectImage = ({
  onComplete,
  sx,
  label = "Upload Project Images",
  multiple = false,
  currentCount = 0,
  maxCount = Infinity,
  showPlaceholder = false,
}: {
  onComplete: SingleImageCallback | MultipleImagesCallback;
  sx?: SxProps;
  label?: string;
  multiple?: boolean;
  currentCount?: number;
  maxCount?: number;
  showPlaceholder?: boolean;
}) => {
  const { showError, showSuccess } = useNotification();
  const accentColor = "#00A06D"; // Always green for the plus icon
  const [isCompressing, setIsCompressing] = useState(false);

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const remainingCapacity = maxCount - currentCount;
    if (remainingCapacity <= 0) {
      showError(`You can upload up to ${maxCount} images for this project.`);
      event.target.value = "";
      return;
    }

    // Filter valid image types first
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const validFiles = Array.from(files).filter((file) => {
      if (!validTypes.includes(file.type)) {
        showError(`${file.name} is not a valid format (JPEG or PNG only)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      event.target.value = '';
      return;
    }

    // Limit to remaining capacity
    const filesToProcess = validFiles.slice(0, remainingCapacity);
    if (validFiles.length > remainingCapacity) {
      showError(`Only ${remainingCapacity} more image${remainingCapacity > 1 ? 's' : ''} can be added (maximum ${maxCount}).`);
    }

    setIsCompressing(true);

    try {
      // Compress all images
      const compressionResults = await compressImages(
        filesToProcess,
        MAX_FILE_SIZE,
      );

      // Create image objects with compressed files
      // Important: Ensure the file is a proper File instance and create blob URL from it
      const validImages: { file: File; local_url: string }[] = compressionResults.map((result, index) => {
        // Ensure we're using the final compressed file (which may have been renamed)
        let finalFile: File | Blob = result.compressed;
        
        // Double-check it's a File instance, not a Blob
        if (!(finalFile instanceof File)) {
          // If somehow it's a Blob, convert it to File
          const originalFile = filesToProcess[index];
          const fileName = originalFile?.name || `image-${Date.now()}.jpg`;
          const fileType = finalFile.type || 'image/jpeg';
          finalFile = new File([finalFile], fileName, { type: fileType });
        }
        
        // At this point, finalFile should be a File
        const file = finalFile as File;
        const local_url = URL.createObjectURL(file);
        
        return {
          file, // Use compressed file
          local_url,
        };
      });

      // Show compression success message
      if (compressionResults.length > 0) {
        const totalSaved = compressionResults.reduce(
          (sum, r) => sum + (r.originalSize - r.compressedSize),
          0
        );
        if (totalSaved > 0) {
          const savedMB = (totalSaved / (1024 * 1024)).toFixed(2);
          showSuccess(`Images compressed successfully. Saved ${savedMB}MB.`, { duration: 3000 });
        }
      }

      // Add valid images
      if (validImages.length > 0) {
        if (multiple) {
          (onComplete as MultipleImagesCallback)(validImages);
        } else {
          // For single mode, only use the first valid image
          (onComplete as SingleImageCallback)(validImages[0]);
        }
      }
    } catch (error: any) {
      showError(error.message || 'Failed to compress images. Please try again.', { duration: 8000 });
    } finally {
      setIsCompressing(false);
      // Reset input to allow selecting the same file again
      event.target.value = '';
    }
  };

  return (
    <Box>
      <ButtonBase
        sx={{
          width: "100%",
          backgroundColor: "#FBFBFB", // Standardized light grey background
          height: "100%",
          minHeight: "164px",
          border: "1px dashed #D9D9D9", // Dashed border
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          maxWidth: "100%", // Ensure it takes full width
          ...sx,
        }}
        component="label"
      >
        <Stack spacing={1.5} alignItems="center" textAlign="center" p={2} width="100%">
          {isCompressing ? (
            <>
              <CircularProgress size={40} sx={{ color: accentColor }} />
              <Body1Typography component="span" sx={{ fontWeight: 600, color: "#000" }}>
                Compressing images...
              </Body1Typography>
              <Body2Typography 
                component="span" 
                sx={{ 
                  fontSize: "12px !important", 
                  color: "#666666",
                  lineHeight: 1.4
                }}
              >
                Please wait while we optimize your images
              </Body2Typography>
            </>
          ) : (
            <>
              <AddIcon fontSize="large" sx={{ color: accentColor }} />
              <Body1Typography component="span" sx={{ fontWeight: 600, color: "#000" }}>
                Click to choose a file
              </Body1Typography>
              <Body2Typography 
                component="span" 
                sx={{ 
                  fontSize: "12px !important", 
                  color: "#666666",
                  lineHeight: 1.4
                }}
              >
                JPEG and PNG formats, up to 5MB. Images will be automatically compressed.
                {maxCount !== Infinity && " "}
                {maxCount !== Infinity ? `Maximum ${maxCount} images per project.` : ""}
              </Body2Typography>
              <VisuallyHiddenInput 
                type="file" 
                onChange={onChange}
                multiple={multiple}
                accept="image/jpeg,image/jpg,image/png"
                disabled={isCompressing}
                aria-label={label}
                name={(label || "upload-image").toLowerCase().replace(/\s+/g, "-")}
                id={(label || "upload-image").toLowerCase().replace(/\s+/g, "-")}
              />
            </>
          )}
        </Stack>
      </ButtonBase>
    </Box>
  );
};
